import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { ClientContact } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, formatDateTime, APP_CONFIG } from '../../config/appConfig';
import ContactPointsEditor from '../address-book/ContactPointsEditor';
import ContactPointsDisplay from '../address-book/ContactPointsDisplay';
import {
  ContactPointRow,
  contactPointsToRows,
  contactPointsFilterText,
  hasContactPoints,
  mergeContactPointPayload,
} from '../address-book/contactPoints';
import type { ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  deleteActionClass,
  primaryLinkClass,
} from '../address-book/themeHelpers';

type ClientContactFormData = Partial<ClientContact> & {
  contactEmails?: ContactPointRow[];
  contactPhones?: ContactPointRow[];
};

function prepareContactSaveData(data: ClientContactFormData) {
  const { contactEmails, contactPhones, ...rest } = data;
  return {
    ...rest,
    contactPoints: mergeContactPointPayload(contactEmails, contactPhones),
  };
}

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'People',
    description: 'Individuals in your address book',
    i18nKey: 'client-contacts',
    permissionModuleKey: 'clients',
    route: {
      list: '/clients',
      new: '/clients',
      detail: (id: number) => `/clients/${id}`,
      edit: (id: number) => `/clients/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/client-contacts',
      detail: (id: number) => `/client-contacts/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search people by name, email, phone, or position...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientContactFirstName', 'clientContactLastName', 'clientContactPosition', 'clientContactEmail', 'clientContactPhone', 'clientId'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientContactFirstName',
        label: 'First Name',
        placeholder: 'All First Names',
        type: 'text' as const,
        getValue: (item: ClientContact) => item.clientContactFirstName,
        options: [],
      },
      {
        key: 'clientContactLastName',
        label: 'Last Name',
        placeholder: 'All Last Names',
        type: 'text' as const,
        getValue: (item: ClientContact) => item.clientContactLastName,
        options: [],
      },
      {
        key: 'clientContactPosition',
        label: 'Position',
        placeholder: 'All Positions',
        type: 'text' as const,
        getValue: (item: ClientContact) => item.clientContactPosition,
        options: [],
      },
      {
        key: 'clientContactEmail',
        label: 'Email',
        placeholder: 'All Emails',
        type: 'text' as const,
        getValue: (item: ClientContact) => contactPointsFilterText(item.contactPoints, 'EMAIL'),
        options: [],
      },
      {
        key: 'clientContactPhone',
        label: 'Phone',
        placeholder: 'All Phones',
        type: 'text' as const,
        getValue: (item: ClientContact) => contactPointsFilterText(item.contactPoints, 'PHONE'),
        options: [],
      },
      {
        key: 'clientId',
        label: 'Organization',
        placeholder: 'All organizations',
        type: 'select' as const,
        getValue: (item: ClientContact) =>
          item.client?.clientName || (item.clientId ? `Organization #${item.clientId}` : ''),
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: ClientContact[],
      filters: {
        clientContactFirstName: string[] | string;
        clientContactLastName: string[] | string;
        clientContactPosition: string[] | string;
        clientContactEmail: string[] | string;
        clientContactPhone: string[] | string;
        clientId: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      return items.filter((item) => {
        // First Name filter
        const firstNameFilter = filters.clientContactFirstName;
        if (typeof firstNameFilter === 'string' && firstNameFilter.trim() !== '') {
          const value = (item.clientContactFirstName || '').toLowerCase();
          if (!value.includes(firstNameFilter.toLowerCase())) return false;
        } else if (Array.isArray(firstNameFilter) && firstNameFilter.length > 0) {
          const value = (item.clientContactFirstName || '').toLowerCase();
          const matches = firstNameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Last Name filter
        const lastNameFilter = filters.clientContactLastName;
        if (typeof lastNameFilter === 'string' && lastNameFilter.trim() !== '') {
          const value = (item.clientContactLastName || '').toLowerCase();
          if (!value.includes(lastNameFilter.toLowerCase())) return false;
        } else if (Array.isArray(lastNameFilter) && lastNameFilter.length > 0) {
          const value = (item.clientContactLastName || '').toLowerCase();
          const matches = lastNameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Position filter
        const positionFilter = filters.clientContactPosition;
        if (typeof positionFilter === 'string' && positionFilter.trim() !== '') {
          const value = (item.clientContactPosition || '').toLowerCase();
          if (!value.includes(positionFilter.toLowerCase())) return false;
        } else if (Array.isArray(positionFilter) && positionFilter.length > 0) {
          const value = (item.clientContactPosition || '').toLowerCase();
          const matches = positionFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Email filter
        const emailFilter = filters.clientContactEmail;
        if (typeof emailFilter === 'string' && emailFilter.trim() !== '') {
          const value = contactPointsFilterText(item.contactPoints, 'EMAIL');
          if (!value.includes(emailFilter.toLowerCase())) return false;
        } else if (Array.isArray(emailFilter) && emailFilter.length > 0) {
          const value = contactPointsFilterText(item.contactPoints, 'EMAIL');
          const matches = emailFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Phone filter
        const phoneFilter = filters.clientContactPhone;
        if (typeof phoneFilter === 'string' && phoneFilter.trim() !== '') {
          const value = contactPointsFilterText(item.contactPoints, 'PHONE');
          if (!value.includes(phoneFilter.toLowerCase())) return false;
        } else if (Array.isArray(phoneFilter) && phoneFilter.length > 0) {
          const value = contactPointsFilterText(item.contactPoints, 'PHONE');
          const matches = phoneFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Organization filter
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Organization #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter ||
              (item.clientId && filter === `Organization #${item.clientId}`) ||
              (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        return true;
      });
    },
  },

  // Form Fields Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Organization',
      type: 'select',
      required: true,
      options: [],
      apiEndpoint: '/crm-clients',
      labelField: 'clientName',
      validation: {
        required: true,
      },
    },
    {
      name: 'clientContactFirstName',
      label: 'First Name',
      type: 'text',
      required: false,
      validation: {
        required: false,
      },
    },
    {
      name: 'clientContactLastName',
      label: 'Last Name',
      type: 'text',
      required: false,
      validation: {
        required: false,
      },
    },
    {
      name: 'clientContactPosition',
      label: 'Position',
      type: 'text',
      required: false,
      validation: {
        required: false,
      },
    },
    {
      name: 'contactEmails',
      label: '',
      type: 'custom',
      customRender: ({ value, onChange }) => (
        <ContactPointsEditor
          kind="EMAIL"
          label="Emails"
          value={(value as ContactPointRow[]) ?? []}
          onChange={onChange}
        />
      ),
    },
    {
      name: 'contactPhones',
      label: '',
      type: 'custom',
      customRender: ({ value, onChange }) => (
        <ContactPointsEditor
          kind="PHONE"
          label="Phones"
          value={(value as ContactPointRow[]) ?? []}
          onChange={onChange}
        />
      ),
    },
    {
      name: 'clientContactNotes',
      label: 'Notes',
      type: 'textarea',
      required: false,
      rows: 4,
      validation: {
        required: false,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Contact Information',
        icon: '👤',
        gridCols: 'md:grid-cols-12',
        fields: ['clientId', 'clientContactFirstName', 'clientContactLastName', 'clientContactPosition'],
        fieldSpans: {
          clientId: 'md:col-span-6',
          clientContactFirstName: 'md:col-span-6',
          clientContactLastName: 'md:col-span-6',
          clientContactPosition: 'md:col-span-6',
        },
        collapsible: true,
      },
      {
        id: 'contact',
        title: 'Contact Details',
        icon: '☎️',
        gridCols: 'md:grid-cols-12',
        fields: ['contactEmails', 'contactPhones', 'clientContactNotes'],
        fieldSpans: {
          contactEmails: 'md:col-span-12',
          contactPhones: 'md:col-span-12',
          clientContactNotes: 'md:col-span-12',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: ClientContact) => item.clientContactId,
    getClientId: (item: ClientContact) => item.clientId ?? null,
  },

  // Toolbar Actions
  toolbar: {
    actions: [],
  },

  // Empty States
  emptyStates: {
    noData: 'No people in your address book yet.',
    noSearchResults: 'No people match your search',
    noFilterResults: 'No people match your filters',
  },

  // Delete Configuration
  delete: {
    title: 'Delete person',
    message: 'Are you sure you want to remove this person from your address book?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete contact',
    handler: async (id: number) => {
      const { deleteClientContact } = await import('./useData');
      await deleteClientContact(id);
    },
  },

  // Detail Configuration
  detail: {
    subtitle: 'Person details',
    notFoundTitle: 'Person not found',
    notFoundMessage: 'The contact you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to address book',
    defaultTitle: 'Contact',
    getTitle: (item: ClientContact) => {
      const name = `${item.clientContactFirstName || ''} ${item.clientContactLastName || ''}`.trim();
      return name || `Contact #${item.clientContactId}`;
    },
  },

  // Utility Functions
  utils: {
    formatDate: formatDateGlobal,
    formatDateTime: formatDateTime,
  },

  // Get Columns Function
  getColumns: (
    formatDate: (dateString?: string) => string,
    _opts?: unknown,
    colors?: ThemeColors,
  ): Column<ClientContact>[] => [
    {
      key: 'clientContactId',
      header: 'ID',
      render: (_value: unknown, row: ClientContact) => row.clientContactId,
      sortable: true,
    },
    {
      key: 'name',
      header: 'Name',
      render: (_value: unknown, row: ClientContact) => {
        const firstName = row.clientContactFirstName || '';
        const lastName = row.clientContactLastName || '';
        const name = `${firstName} ${lastName}`.trim();
        return name || '-';
      },
      sortable: false,
    },
    {
      key: 'clientContactPosition',
      header: 'Position',
      render: (_value: unknown, row: ClientContact) => row.clientContactPosition || '-',
      sortable: false,
    },
    {
      key: 'clientContactEmail',
      header: 'Email',
      render: (_value: unknown, row: ClientContact) => (
        <ContactPointsDisplay
          points={row.contactPoints}
          kind="EMAIL"
          compact
        />
      ),
      sortable: false,
    },
    {
      key: 'clientContactPhone',
      header: 'Phone',
      render: (_value: unknown, row: ClientContact) => (
        <ContactPointsDisplay
          points={row.contactPoints}
          kind="PHONE"
          compact
        />
      ),
      sortable: false,
    },
    {
      key: 'clientId',
      header: 'Organization',
      render: (_value: unknown, row: ClientContact) => {
        if (row.clientId && row.client) {
          return (
            <Link
              to={`/clients/${row.clientId}`}
              className={primaryLinkClass(colors)}
              onClick={(e) => e.stopPropagation()}
            >
              {row.client.clientName || `Organization #${row.clientId}`}
            </Link>
          );
        }
        return row.clientId ? `Organization #${row.clientId}` : '-';
      },
      sortable: false,
    },
    {
      key: 'clientContactNotes',
      header: 'Notes',
      render: (_value: unknown, row: ClientContact) => (
        <TruncatedText text={row.clientContactNotes || ''} maxLength={50} />
      ),
      sortable: false,
    },
  ],

  // Get Toolbar Icon
  getToolbarIcon: (iconName: string): React.ReactNode => {
    const icons: Record<string, React.ReactNode> = {
      add: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      download: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  },

  // Get Table Actions
  getTableActions: (
    item: ClientContact,
    onDelete: (id: number) => void,
    colors?: ThemeColors,
    _b?: unknown,
    options?: { canDelete?: boolean; canEdit?: boolean }
  ): React.ReactNode => {
    const canDelete = options?.canDelete !== false;
    return (
      <div className="flex items-center gap-2">
        {canDelete && (
          <button
            onClick={() => onDelete(item.clientContactId)}
            className={deleteActionClass(colors)}
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    );
  },

  // Get Initial Form Data
  getInitialFormData: (): ClientContactFormData => ({
    clientId: undefined,
    clientContactFirstName: '',
    clientContactLastName: '',
    clientContactPosition: '',
    contactEmails: [],
    contactPhones: [],
    clientContactNotes: '',
  }),

  // Map Item to Form Data
  mapItemToFormData: (item: ClientContact): ClientContactFormData => ({
    clientId: item.clientId,
    clientContactFirstName: item.clientContactFirstName || '',
    clientContactLastName: item.clientContactLastName || '',
    clientContactPosition: item.clientContactPosition || '',
    contactEmails: contactPointsToRows(item.contactPoints, 'EMAIL'),
    contactPhones: contactPointsToRows(item.contactPoints, 'PHONE'),
    clientContactNotes: item.clientContactNotes || '',
  }),

  // Form Configuration
  form: {
    new: {
      title: 'New person',
      subtitle: 'Add someone to your address book',
      submitText: 'Create person',
    },
    edit: {
      title: 'Edit person',
      subtitle: 'Update contact details',
      submitText: 'Update person',
    },
    cancelText: 'Cancel',
    loadingText: 'Saving...',
    errorMessage: 'Failed to save contact',
    create: async (data: Partial<ClientContact>) => {
      const { createClientContact } = await import('./useData');
      return createClientContact(prepareContactSaveData(data as ClientContactFormData) as any);
    },
    update: async (id: number, data: Partial<ClientContact>) => {
      const { updateClientContact } = await import('./useData');
      return updateClientContact(id, prepareContactSaveData(data as ClientContactFormData) as any);
    },
  },
};

// Export functions separately for use in List component
export const getColumns = (
  formatDate: (dateString?: string) => string,
  opts?: unknown,
  colors?: ThemeColors,
): Column<ClientContact>[] =>
  moduleConfig.getColumns(formatDate, opts, colors);

export const getToolbarIcon = (iconName: string): React.ReactNode =>
  moduleConfig.getToolbarIcon(iconName);

export const getTableActions = (
  item: ClientContact,
  onDelete: (id: number) => void,
  colors?: ThemeColors,
  b?: unknown,
  options?: { canDelete?: boolean }
): React.ReactNode =>
  moduleConfig.getTableActions(item, onDelete, colors, b, options);

// Helper function to get detail sections
export const getDetailSections = (
  item: ClientContact,
  formatDate: (dateString?: string) => string,
  _formatCurrency?: (amount?: number | null) => string,
  _mutate?: () => void,
  colors?: ThemeColors,
) => {
  return [
    {
      title: 'Contact Information',
      icon: '👤',
      fields: [
        {
          label: 'ID',
          value: item.clientContactId.toString(),
        },
        {
          label: 'First Name',
          value: item.clientContactFirstName || '-',
        },
        {
          label: 'Last Name',
          value: item.clientContactLastName || '-',
        },
        {
          label: 'Position',
          value: item.clientContactPosition || '-',
        },
        {
          label: 'Organization',
          value: item.client?.clientName ? (
            <Link
              to={`/clients/${item.clientId}`}
              className={primaryLinkClass(colors)}
            >
              {item.client.clientName}
            </Link>
          ) : (item.clientId ? `Client #${item.clientId}` : '-'),
        },
      ],
    },
    {
      title: 'Contact Details',
      icon: '📞',
      fields: [
        {
          label: 'Emails',
          value: (
            <ContactPointsDisplay
              points={item.contactPoints}
              kind="EMAIL"
            />
          ),
        },
        {
          label: 'Phones',
          value: (
            <ContactPointsDisplay
              points={item.contactPoints}
              kind="PHONE"
            />
          ),
        },
        {
          label: 'Notes',
          value: item.clientContactNotes || '-',
        },
        {
          label: 'Created At',
          value: item.clientContactCreatedAt ? formatDateTime(item.clientContactCreatedAt) : '-',
        },
        {
          label: 'Updated At',
          value: item.clientContactUpdatedAt ? formatDateTime(item.clientContactUpdatedAt) : '-',
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: ClientContact) => {
  return null; // No status badges for contacts
};

export type ModuleConfig = typeof moduleConfig;
